/**
 * Grading-scheme GENERATION — ONE JOB PER TOP-LEVEL MAIN TASK.
 *
 * The counterpart of `ms-generation.job.ts`; everything said there about why the unit is a main task, what
 * the split replaces (a per-process `watchQuery` and an in-memory in-flight Set) and how a root is persisted
 * so that roots can run in parallel applies here unchanged.
 *
 * One extra write of its own: newly generated GRAMMAR rules. Those are exam-level, not per root, and are now
 * appended with an atomic `$concatArrays` update — see the persist block in `gs-generation-agent.ts`. The
 * previous read-modify-write there claimed to preserve a concurrent root's rules and could not; with roots
 * actually running concurrently it had to.
 *
 * The one thing that is different is the reason this type benefits MOST from the split: a GS root waits on
 * ITS OWN subtree's master solutions, not on the exam's master-solution run as a whole
 * (`subtreeMasterSolutionReady`). As one job per root with a per-root `ready`, root 3's grading scheme starts
 * the moment root 3 has a master solution — the pipeline interleaves instead of running MS-then-GS.
 */

import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../../exam.js';
import {
  generateGradingSchemeForRootTask,
  openGradingSchemeGenerationRun,
  rootNeedsGsWork,
} from '../../agents/gs-generation-agent.js';
import {
  getTaskKey,
  gsTargets,
  subtreeMasterSolutionReady,
  type NestedExamTask,
} from '../../agents/tree-generation-utils.js';
import { gradingSchemeStep } from '../steps/grading-scheme.step.js';
import * as conditions from '../job-conditions.js';
import { taskTreeLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface GsGenerationPayload {
  /** `getTaskKey` of the top-level main task this job generates. */
  taskKey: string;
  /** Every gradable node under this root still missing CONTENT (`gsTargets`), computed once here at
   * dispatch time. Informational only, read by `get-live-solution-jobs.ts` for the ms-gs canvas's per-box
   * spinner — nothing in this job definition consults it. A node that already has content but is still
   * missing its languageUse share is NOT included (that set isn't known until the handler actually runs
   * `computeLanguageUseBackfillForRoot`), so a generation that only backfills languageUse for such a node
   * won't spin its languageUse box — an accepted, minor gap, not chased further. */
  targetTaskIds: string[];
  [key: string]: unknown;
}

const languageUseRulesOf = (exam: { languageUseSchemeRules?: unknown }): any[] =>
  Array.isArray(exam.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];

export const gradingSchemeGenerationJob = defineJob<GsGenerationPayload>({
  type: 'gsGeneration',

  /**
   * One row per root that has no grading scheme yet — see `ms-generation.job.ts` for why this is enumerated here
   * rather than derived from the exam.
   *
   * ENQUEUED WHETHER OR NOT the root's master solution has landed. A root waiting on one still needs a row; it just
   * is not runnable yet, and `ready()` holds it at `master-solution-not-ready` until the `msGeneration` row that
   * fills it completes and nudges it awake. Filtering here instead would drop the row for good.
   */
  /** See `ms-generation.job.ts`: never a repeat, and the root total is known here. */
  onStepStart: { reupload: false, open: examId => openGradingSchemeGenerationRun(examId) },

  batch: {
    units: async examId => {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      const rules = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
      const roots = ((exam?.tasks ?? []) as NestedExamTask[]).filter(root => rootNeedsGsWork(root, rules));
      return roots.map(root => ({
        taskKey: getTaskKey(root),
        targetTaskIds: gsTargets(root).map(s => getTaskKey(s.node)),
      }));
    },
  },

  /** The run is supplied by `dispatchGradingSchemeGeneration` — see `ms-generation.job.ts`. */
  onStepComplete: gradingSchemeStep,

  /** Per root — same reasoning and same `_id` caveat as `ms-generation.job.ts`. */
  lockKey: (examId, payload) =>
    payload.taskKey.startsWith('task:') ? taskTreeLockKey(examId) : `${taskTreeLockKey(examId)}:${payload.taskKey}`,

  dedupeKey: (examId, payload) => `gsGeneration:${examId}:${payload.taskKey}`,

  ready: (exam, job) => {
    if (!conditions.hasTasks(exam)) return { verdict: 'wait', because: 'gsGeneration:no-tasks' };

    // Same producer collision as in `ms-generation.job.ts`: an uploaded grading scheme being extracted right
    // now writes the very field this would generate — and it also `$set`s `languageUseSchemeRules` wholesale,
    // which would drop rules this generation had appended. Wait it out.
    if (conditions.artifactExtractionRunning(exam, 'gradingScheme')) {
      return { verdict: 'wait', because: 'gsGeneration:extraction-running' };
    }

    const root = ((exam.tasks ?? []) as unknown as NestedExamTask[]).find(
      task => getTaskKey(task) === job.payload.taskKey,
    );
    if (!root) return { verdict: 'drop', because: 'gsGeneration:root-gone' };
    if (!rootNeedsGsWork(root, languageUseRulesOf(exam))) {
      return { verdict: 'drop', because: 'gsGeneration:already-covered' };
    }
    // The per-root gate this whole split exists for. A wait, not a drop: the master solution for this subtree
    // is very likely still being generated or extracted right now.
    if (!subtreeMasterSolutionReady(root)) {
      return { verdict: 'wait', because: 'gsGeneration:master-solution-not-ready' };
    }
    return 'run';
  },

  maxRuntimeMs: 10 * 60_000,
  maxAttempts: 3,

  // NO `desired` — see `ms-generation.job.ts`. `dispatchGradingSchemeGeneration` enumerates the roots, and
  // deliberately does not filter them by master-solution readiness: a root still waiting for one needs a row,
  // it just is not runnable yet, and the `master-solution-not-ready` wait above is what holds it until the
  // msGeneration row that fills it completes and nudges it awake.

  // The run is closed by `onTransition`, not here — see `ms-generation.job.ts`.
  handler: async (examId, payload) => {
    const result = await generateGradingSchemeForRootTask(examId, payload.taskKey);
    if (!result.generated) return { skipped: true, note: result.skipped ?? 'nothing to generate' };
    return { note: 'generated' };
  },
});
